import { createContext, useState, useEffect, useContext } from "react";
import {
  fetchNotifications,
  markNotificationAsRead as apiMarkNotificationAsRead,
} from "../api";
import { useAuthContext } from "./context";
import PropTypes from "prop-types";

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [notificationError, setNotificationError] = useState(null);

  const { userData } = useAuthContext();

  const loadNotifications = async () => {
    setLoadingNotifications(true);
    setNotificationError(null);
    try {
      if (!userData) return;

      const data = await fetchNotifications();

      setNotifications(data);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setNotificationError("Failed to load notifications");
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Fetch notifications on component mount
  useEffect(() => {
    loadNotifications();
  }, [userData]);

  // Optional: Refetch notifications when the user becomes online or focuses the tab
  useEffect(() => {
    const handleFocus = () => {
      console.log("Window focused, reloading notifications...");
      loadNotifications();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Function to mark a notification as read via API and update state
  const markNotificationAsRead = async (notificationId) => {
    try {
      await apiMarkNotificationAsRead(notificationId);
      // Update the local state to reflect the change immediately
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
      // Optionally, show an error toast
    }
  };

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loadingNotifications,
        notificationError,
        loadNotifications, // Expose load function if needed
        markNotificationAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the NotificationContext
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === null) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
